import { useState } from "react"
import { Check, Percent, Trash2, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatMoney } from "@/lib/formatters"
import {
  deleteInline,
  patchInline,
  postInline,
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
} from "@/lib/inline-update"
import { cn } from "@/lib/utils"
import {
  COURTESY_KIND,
  DISCOUNT_PATHS,
  discountBlockedKey,
  discountRequestComplete,
  discountRequestLabel,
  discountValueLabelKey,
  discountValueParam,
  supersedeNames,
  supersedeNoticeKey,
  tDiscount,
  type AppliedDiscount,
  type DiscountEligibility,
  type DiscountKindOption,
  type DiscountReasonOption,
  type DiscountSupersede,
} from "@/features/staff/discounts"

/**
 * Taking money off, from the row it comes off.
 *
 * A ceremony rather than an inline commit, for the three reasons the design
 * system already gives: it is not undoable by typing (a discount is reversed by
 * voiding it, which is its own audited act), it moves money the restaurant will
 * never collect, and it needs one piece of information only a human has — the
 * reason. `Location::DiscountPolicy` owns that catalog; this select renders
 * whatever the server sends and nothing else, because a report nobody can group
 * by is a report nobody runs.
 *
 * **A courtesy has no amount to type.** Choosing it hides the value field and
 * says the whole line goes to zero. Rule 2 of the policy is that a courtesy is a
 * giveaway rather than a large discount; leaving the field on screen with 100 in
 * it is an invitation to type 90 and route around the distinction that keeps
 * giveaways out of revenue.
 *
 * **The dialog says who decides, before the operator commits.** A waiter's
 * discount lands pending and changes no total until a manager resolves it, and
 * the sentence above the buttons says exactly that. Promising a diner a discount
 * and discovering afterwards that it needs a manager is the failure
 * `docs/product.md` names outright.
 *
 * **And it says what this discount destroys, in pesos.** A person's decision
 * supersedes a rule's, so applying a discount over a promoted line removes the
 * promotion — and a bill-wide promotion comes off every line, not just this one,
 * because `apportionment` has no partial-void path on the server. A manager
 * weighing whether to comp one dish has to know it costs the table its whole
 * happy hour, and has to know the number. The figure is
 * `Staff::TablesWorkspaceSerializer`'s, computed from the same rows
 * `Location::DiscountApplyService` will supersede under the bill lock; this
 * dialog renders it and never works it out for itself.
 */

type DiscountActionProps = {
  billId: number
  /** A single line, or the whole bill. It changes the copy, not the endpoint. */
  scope: "line" | "bill"
  orderId?: number | null
  /** Straight from `Location::DiscountEligibility`. Absent means "no control". */
  eligibility?: DiscountEligibility | null
  kinds: DiscountKindOption[]
  reasons: DiscountReasonOption[]
  currency: string
}

export function DiscountAction({
  billId,
  scope,
  orderId,
  eligibility,
  kinds,
  reasons,
  currency,
}: DiscountActionProps) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState("")
  const [value, setValue] = useState("")
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  if (!eligibility?.allowed) return null

  const isCourtesy = kind === COURTESY_KIND
  const isBillScope = scope === "bill"
  const ceiling = formatMoney(eligibility.maxDiscountableCents / 100, { currency })
  const supersedes = eligibility.supersedes ?? null

  const reset = () => {
    setKind("")
    setValue("")
    setReason("")
  }

  const handleOpenChange = (next: boolean) => {
    // Escape and the overlay both route through here. A dialog that vanishes
    // mid-request leaves the operator with no idea whether the money came off.
    if (submitting) return

    setOpen(next)
    if (!next) reset()
  }

  const handleConfirm = () => {
    if (submitting || !discountRequestComplete(kind, value, reason)) return

    setSubmitting(true)
    postInline(
      DISCOUNT_PATHS.create(billId),
      {
        kind,
        value: discountValueParam(kind, value),
        reason,
        order_id: orderId ?? null,
      },
      // `preserveState` keeps the dialog mounted through the response, so a
      // refusal the server reports as a flash alert — a charge that started
      // between this render and the confirm, most of all — lands while the
      // operator is still looking at what they chose.
      { preserveState: true }
    )
      .then((result) => {
        setOpen(false)
        reset()
        toastInlineUpdateSuccess(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  return (
    <>
      {isBillScope ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="flex-1 gap-1.5"
          onClick={() => handleOpenChange(true)}
          data-testid="discount-open"
          data-scope="bill"
        >
          <Percent className="size-3.5" />
          {tDiscount("open_bill")}
        </Button>
      ) : (
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={() => handleOpenChange(true)}
          data-testid="discount-open"
          data-scope="line"
          data-order-id={orderId ?? undefined}
        >
          <Percent className="size-3.5" />
          <span className="sr-only">{tDiscount("open")}</span>
        </Button>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent data-testid="discount-dialog" data-order-id={orderId ?? undefined}>
          <DialogHeader>
            <DialogTitle>{tDiscount(isBillScope ? "title_bill" : "title")}</DialogTitle>
            <DialogDescription>{tDiscount("description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* The ceiling is the server's figure or it is nothing: a bill-level
                payload that carries no eligibility gets no number here rather
                than one this screen worked out for itself. */}
            {eligibility.maxDiscountableCents > 0 ? (
              <div className="flex items-baseline justify-between gap-4 rounded-md border px-3 py-2">
                <span className="text-sm text-muted-foreground">{tDiscount("original_price")}</span>
                <span
                  className="text-sm font-semibold tabular-nums text-foreground"
                  data-testid="discount-ceiling"
                >
                  {ceiling}
                </span>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="discount-kind-field">{tDiscount("kind_label")}</Label>
              <Select
                value={kind}
                onValueChange={(next) => {
                  setKind(next)
                  // The value belongs to the kind that asked for it. Carrying a
                  // percentage over into "monto fijo" is how 20 becomes twenty
                  // pesos off without anyone retyping it.
                  setValue("")
                }}
              >
                <SelectTrigger id="discount-kind-field" className="w-full" data-testid="discount-kind">
                  <SelectValue placeholder={tDiscount("kind_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {kinds.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`discount-kind-option-${option.value}`}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* A courtesy is the whole line by construction, so there is nothing
                to type and no field to type it in. */}
            {isCourtesy ? (
              <p
                className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
                data-testid="discount-courtesy-notice"
              >
                {tDiscount("courtesy_notice")}
              </p>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="discount-value-field">
                  {tDiscount(discountValueLabelKey(kind))}
                </Label>
                <Input
                  id="discount-value-field"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  max={kind === "fixed_amount" ? undefined : 100}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  placeholder={tDiscount("value_placeholder")}
                  disabled={!kind}
                  data-testid="discount-value"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="discount-reason-field">{tDiscount("reason_label")}</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger
                  id="discount-reason-field"
                  className="w-full"
                  aria-describedby="discount-reason-hint"
                  data-testid="discount-reason"
                >
                  <SelectValue placeholder={tDiscount("reason_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`discount-reason-option-${option.value}`}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p id="discount-reason-hint" className="text-xs text-muted-foreground">
                {tDiscount("reason_hint")}
              </p>
            </div>

            {/* What this costs the table, said before the operator commits.
                Above the "who decides" line because it outranks it: needing a
                manager delays the discount, this one removes money the diner was
                already promised. */}
            {supersedes ? (
              <SupersedeNotice supersede={supersedes} scope={scope} currency={currency} />
            ) : null}

            {/* Who decides, said before the operator commits rather than after. */}
            {eligibility.requiresAuthorization ? (
              <p
                className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
                data-testid="discount-pending-notice"
              >
                {tDiscount("requires_authorization")}
              </p>
            ) : (
              <p
                className="rounded-md border p-3 text-sm text-muted-foreground"
                data-testid="discount-immediate-notice"
              >
                {tDiscount("applies_immediately")}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => handleOpenChange(false)}
              data-testid="discount-cancel"
            >
              {tDiscount("cancel")}
            </Button>
            <Button
              type="button"
              disabled={submitting || !discountRequestComplete(kind, value, reason)}
              onClick={handleConfirm}
              data-testid="discount-confirm"
            >
              {submitting ? tDiscount("submitting") : tDiscount("confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * The promotions this discount is about to remove, and what they are worth.
 *
 * The figure is the server's, rendered as given. It is the one number in this
 * dialog nobody can reconstruct by eye: a bill-wide promotion is apportioned
 * across every line, so "how much does the table lose" is not visible anywhere
 * on the ticket, and `Location::DiscountApplyService` voids it wholesale the
 * moment this confirm button is pressed.
 *
 * Colour lives in the dot, per the design system; the amount stays a figure —
 * foreground, semibold, tabular.
 */
function SupersedeNotice({
  supersede,
  scope,
  currency,
}: {
  supersede: DiscountSupersede
  scope: "line" | "bill"
  currency: string
}) {
  return (
    <div
      className="space-y-1 rounded-md border border-destructive/40 bg-destructive/10 p-3"
      data-testid="discount-supersede-notice"
      data-bill-level={supersede.billLevel ? "true" : "false"}
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-destructive" />
        {tDiscount("supersede_title")}
      </p>
      <p className="text-sm text-foreground">
        {tDiscount(supersedeNoticeKey(supersede, scope), {
          names: supersedeNames(supersede),
        })}
      </p>
      <p className="flex items-baseline justify-between gap-4 text-sm">
        <span className="text-muted-foreground">{tDiscount("supersede_amount_label")}</span>
        <span
          className="font-semibold tabular-nums text-foreground"
          data-testid="discount-supersede-amount"
        >
          {formatMoney(supersede.amount, { currency })}
        </span>
      </p>
    </div>
  )
}

/**
 * Why there is no control on this line.
 *
 * Rendered once per bill rather than once per row: every refusal
 * `Location::DiscountEligibility` reports at bill level — a charge in flight, a
 * split settling, a cancelled bill — is true of every line at once, and repeating
 * it once per row down a busy panel is noise, not honesty.
 */
export function DiscountBlockedNotice({ eligibility }: { eligibility: DiscountEligibility }) {
  return (
    <p className="px-4 pb-2 text-xs text-muted-foreground" data-testid="discount-blocked">
      {tDiscount(discountBlockedKey(eligibility.reasonCode))}
    </p>
  )
}

/**
 * What a waiter asked for and nobody has decided yet.
 *
 * `--warning`, because a pending discount is a state that needs a human rather
 * than a failure — and the figure shown is what was *requested*, not the row's
 * `amount`, which is 0.00 until a manager resolves it. Approve and Reject render
 * only for someone who may actually decide (`can_authorize_discounts?`); for
 * everyone else the strip is the honest answer to "did my discount go through".
 */
export function DiscountPendingStrip({
  discounts,
  currency,
  canAuthorize,
}: {
  discounts: AppliedDiscount[]
  currency: string
  canAuthorize: boolean
}) {
  const [resolving, setResolving] = useState<number | null>(null)

  if (discounts.length === 0) return null

  const resolve = (discountId: number, path: string) => {
    if (resolving !== null) return

    setResolving(discountId)
    patchInline(path, {}, { preserveState: true })
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setResolving(null))
  }

  return (
    <section
      className="mx-4 my-3 space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-3"
      data-testid="discount-pending-strip"
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-warning" />
        {tDiscount("pending_title")}
      </p>

      <ul className="space-y-1.5">
        {discounts.map((discount) => (
          <li
            key={discount.id}
            className="flex items-center gap-2 text-xs"
            data-testid={`discount-pending-${discount.id}`}
          >
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {tDiscount("pending_line", {
                actor: discount.actorName ?? tDiscount("unknown_actor"),
                detail: `${discount.orderName ?? tDiscount("pending_bill_scope")} · ${discount.reasonLabel}`,
              })}
            </span>
            <span className="shrink-0 font-semibold tabular-nums text-foreground">
              {discountRequestLabel(discount, currency)}
            </span>

            {canAuthorize ? (
              <span className="flex shrink-0 items-center gap-0.5">
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  disabled={resolving !== null}
                  onClick={() => resolve(discount.id, DISCOUNT_PATHS.approve(discount.id))}
                  data-testid={`discount-approve-${discount.id}`}
                >
                  <Check className="size-3.5" />
                  <span className="sr-only">{tDiscount("approve")}</span>
                </Button>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  disabled={resolving !== null}
                  onClick={() => resolve(discount.id, DISCOUNT_PATHS.reject(discount.id))}
                  data-testid={`discount-reject-${discount.id}`}
                >
                  <X className="size-3.5" />
                  <span className="sr-only">{tDiscount("reject")}</span>
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * Putting a discount back.
 *
 * Voiding releases the line — `index_one_live_discount_per_order` only counts
 * live rows — so a wrong discount is corrected by voiding and applying again,
 * not by editing one. Anyone who may apply a discount may void one, which is the
 * server's rule (`Location::DiscountVoidService` asks `can_apply_discounts?`)
 * and the only one that leaves a waiter able to undo their own mistake.
 */
export function DiscountVoidButton({
  discountId,
  className,
}: {
  discountId: number
  className?: string
}) {
  const [submitting, setSubmitting] = useState(false)

  const handleClick = () => {
    if (submitting) return

    setSubmitting(true)
    deleteInline(DISCOUNT_PATHS.void(discountId), {}, { preserveState: true })
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  return (
    <Button
      type="button"
      size="icon-sm"
      variant="ghost"
      className={cn("shrink-0", className)}
      disabled={submitting}
      onClick={handleClick}
      data-testid={`discount-void-${discountId}`}
    >
      <Trash2 className="size-3.5" />
      <span className="sr-only">{tDiscount("void")}</span>
    </Button>
  )
}
